import { useCallback, useEffect, useMemo } from 'react';
import { useMachine } from '@xstate/react';
import { useApolloClient } from '@frameio/graphql-client';
import { EMPTY_ARRAY } from '@frameio/utils';
import type { ListLoadingStates } from '@frameio/vapor';
import { LEXICAL_SECTION_ID, NLP_SECTION_ID } from '../../../shared/types';
import type { GetQuickSearchResultsV2QueryVariables } from '../../gql/generated/GetQuickSearchResultsV2';
import type { QuickSearchResultSection } from '../../types';
import { quickSearchMultiMachine } from '../quickSearchMultiMachine';
import type { QuickSearchMachineOutput } from '../types';
import TEXT from './generated/TEXT/useQuickSearchMachineMulti';
import { useInitialSearchNodes } from './useInitialSearchNodes';

const INITIAL_LEXICAL_ITEMS_LIMIT = 3;

TEXT.DEFINE({
  /** Section title for standard/lexical search results */
  lexicalSectionTitle: 'Standard Results',
  /** Section title for AI/NLP search results */
  nlpSectionTitle: 'AI Results',
  /** Tooltip text explaining AI search capabilities */
  nlpSectionTooltip: 'AI search surfaces results that go beyond keywords.',
});

type UseQuickSearchMachineMultiProps = {
  requestVariables: GetQuickSearchResultsV2QueryVariables;
  skip: boolean;
  isSearchQueryStale: boolean;
  isInitial: boolean;
};

export const useQuickSearchMachineMulti = ({
  requestVariables,
  skip,
  isSearchQueryStale,
  isInitial,
}: UseQuickSearchMachineMultiProps): QuickSearchMachineOutput => {
  const apolloClient = useApolloClient();

  const { initialLexicalNodes, initialNlpNodes } = useInitialSearchNodes({
    requestVariables,
    skip,
    isInitial,
    listMode: 'MULTI',
  });

  const [state, send] = useMachine(quickSearchMultiMachine, {
    input: {
      apolloClient,
      requestVariables,
      initialLexicalNodes,
      initialNlpNodes,
    },
  });

  useEffect(() => {
    const hasInitialNodes =
      initialLexicalNodes !== undefined || initialNlpNodes !== undefined;
    // Skip sending SEARCH if hydrating from cache (isInitial with cached data)
    if (skip || (isInitial && hasInitialNodes)) return;

    send({
      type: 'SEARCH',
      requestVariables,
    });
  }, [
    requestVariables,
    send,
    skip,
    isInitial,
    initialLexicalNodes,
    initialNlpNodes,
  ]);

  const handleViewAllClick = useCallback(() => {
    send({ type: 'VIEW_ALL' });
  }, [send]);

  const loadingStatus = useMemo((): ListLoadingStates | undefined => {
    if (isSearchQueryStale) {
      return 'loading';
    }

    // If we're in complete state, queries are complete - no loading
    if (state.matches('complete')) {
      // If both queries have errored, show loading-error
      if (
        state.context.lexicalError !== undefined &&
        state.context.nlpError !== undefined
      ) {
        return 'loading-error';
      }

      return undefined;
    }

    if (state.matches({ searching: { parallelQueries: {} } })) {
      // Lexical Query States
      const isLexLoading = state.matches({
        searching: { parallelQueries: { lexicalQuery: 'lexLoading' } },
      });
      const isLexSuccess = state.matches({
        searching: { parallelQueries: { lexicalQuery: 'lexSuccess' } },
      });
      const isLexError = state.matches({
        searching: { parallelQueries: { lexicalQuery: 'lexError' } },
      });

      // NLP Query States
      const isNlpLoading = state.matches({
        searching: { parallelQueries: { nlpQuery: 'nlpLoading' } },
      });

      // Lexical is loading, or lexical has errored and nlp is still loading
      if (isLexLoading || (isLexError && isNlpLoading)) {
        return 'loading';
      }

      // Lexical succeeded but has no results and NLP is still loading
      if (
        isLexSuccess &&
        state.context.lexicalNodes.length === 0 &&
        isNlpLoading
      ) {
        return 'loading';
      }

      // Lexical succeeded with results, NLP is loading
      if (
        isLexSuccess &&
        state.context.lexicalNodes.length > 0 &&
        isNlpLoading
      ) {
        return 'loading-more';
      }
    }

    return undefined;
  }, [state, isSearchQueryStale]);

  /**
   * The SEARCH event is dispatched from an effect, so on the render where
   * requestVariables changes, the machine's context/counts still reflect the
   * previous query for one render. Comparing context.requestVariables (set by
   * the SEARCH transition) to the current requestVariables prop closes that
   * gap so consumers never see "settled" paired with a stale query's counts.
   */
  const hasQueriesSettled = useMemo(() => {
    return (
      state.matches('complete') &&
      state.context.requestVariables === requestVariables
    );
  }, [state, requestVariables]);

  const { lexicalSectionTitle, nlpSectionTitle, nlpSectionTooltip } = TEXT;

  // Build items from machine context
  const items = useMemo(() => {
    const { lexicalNodes, nlpNodes, showAllLexicalResults } = state.context;

    // Return empty if loading
    if (loadingStatus === 'loading') {
      return EMPTY_ARRAY;
    }

    // Determine if we should truncate lexical results
    // Show all if: user clicked "View All" OR NLP has no results (and queries are settled)
    const shouldTruncateLexicalNodes =
      showAllLexicalResults || (nlpNodes.length === 0 && hasQueriesSettled);

    const lexicalItemsToShow = shouldTruncateLexicalNodes
      ? lexicalNodes
      : lexicalNodes.slice(0, INITIAL_LEXICAL_ITEMS_LIMIT);

    const hasMoreItems =
      lexicalNodes.length > INITIAL_LEXICAL_ITEMS_LIMIT &&
      !shouldTruncateLexicalNodes;

    // Build sections - always show lexical results if we have them
    const sections: QuickSearchResultSection[] =
      lexicalItemsToShow.length > 0
        ? [
            {
              id: LEXICAL_SECTION_ID,
              title: lexicalSectionTitle,
              items: lexicalItemsToShow,
              hasMoreItems,
            },
          ]
        : [];

    // Add NLP results if they exist and queries are settled
    if (nlpNodes.length > 0 && hasQueriesSettled) {
      sections.push({
        id: NLP_SECTION_ID,
        title: nlpSectionTitle,
        items: nlpNodes,
        hasMoreItems: false,
        tooltipText: nlpSectionTooltip,
      });
    }

    return sections;
  }, [
    state.context,
    loadingStatus,
    hasQueriesSettled,
    lexicalSectionTitle,
    nlpSectionTitle,
    nlpSectionTooltip,
  ]);

  const standardResultsCount = state.context.lexicalNodes.length;
  const mediaIntelligenceResultsCount = state.context.nlpNodes.length;

  return {
    listMode: 'MULTI',
    items: items as QuickSearchResultSection[],
    loadingStatus,
    handleViewAllClick,
    standardResultsCount,
    mediaIntelligenceResultsCount,
    hasQueriesSettled,
  };
};
